import { Routes } from '@angular/router';
import { authGuard } from './core/guards/auth.guard';
import { nonAuthGuard } from './core/guards/non-auth.guard';
import { CartDisplay } from './core/types/enums';
import { resetPasswordGuard } from './core/guards/reset-password.guard';
import { confirmDeactivateGuard } from './core/guards/confirm-deactivate.guard';
import { customerSelectionRedirectGuard } from './core/guards/customer-selection-redirect.guard';
import { customerResolver } from './core/resolvers/customer.resolver';
import { customerIdGuard } from './core/guards/customer-id.guard';

export const routes: Routes = [
  {
    path: '',
    redirectTo: 'home',
    pathMatch: 'full',
  },
  {
    path: '',
    canActivate: [authGuard, customerIdGuard],
    resolve: { customer: customerResolver },
    runGuardsAndResolvers: 'pathParamsOrQueryParamsChange',
    children: [
      {
        path: 'shop/overview',
        loadComponent: () =>
          import('./pages/order-overview/order-overview.component').then(
            m => m.OrderOverviewComponent
          ),
        data: { pageHeader: true, noShadow: true },
      },
      {
        path: 'departments',
        loadComponent: () =>
          import('./pages/departments/departments.component').then(
            m => m.DepartmentsComponent
          ),
        data: { pageHeader: true, noShadow: true },
      },
      {
        path: 'single-department',
        loadComponent: () =>
          import('./pages/single-department/single-department.component').then(
            m => m.SingleDepartmentComponent
          ),
        canDeactivate: [confirmDeactivateGuard],
        data: { pageHeader: true, noShadow: true },
      },
      {
        path: 'partner-sync',
        loadComponent: () =>
          import('./pages/connext/connext.component').then(
            m => m.ConnextComponent
          ),
        canDeactivate: [confirmDeactivateGuard],
        data: { pageHeader: true, noShadow: true },
      },
      {
        path: 'connext',
        redirectTo: 'partner-sync',
        pathMatch: 'full',
      },
      {
        path: 'residents/:residentId',
        loadComponent: () =>
          import('./pages/residents/residents.component').then(
            m => m.ResidentsComponent
          ),
        canDeactivate: [confirmDeactivateGuard],
        data: { pageHeader: true, noShadow: true },
      },
      {
        path: 'shop',
        loadComponent: () =>
          import('./pages/shop/shop.component').then(m => m.ShopComponent),
        data: { shopHeader: true },
      },
      {
        path: 'shop/last-ordered',
        loadComponent: () =>
          import('./pages/shop-filtered/shop-filtered.component').then(
            m => m.ShopFilteredComponent
          ),
        data: { shopHeader: true },
      },
      {
        path: 'shop/favourites',
        loadComponent: () =>
          import('./pages/shop-filtered/shop-filtered.component').then(
            m => m.ShopFilteredComponent
          ),
        data: { shopHeader: true },
      },
      {
        path: 'shop/search',
        loadComponent: () =>
          import('./pages/shop-filtered/shop-filtered.component').then(
            m => m.ShopFilteredComponent
          ),
        data: { shopHeader: true },
      },
      {
        path: 'shop/promotion',
        loadComponent: () =>
          import('./pages/shop-filtered/shop-filtered.component').then(
            m => m.ShopFilteredComponent
          ),
        data: { shopHeader: true },
      },
      {
        path: 'shop/category',
        loadComponent: () =>
          import('./pages/shop-category/shop-category.component').then(
            m => m.ShopCategoryComponent
          ),
        data: { shopHeader: true },
      },
      {
        path: 'customer-profile',
        loadComponent: () =>
          import('./pages/customer-profile/customer-profile.component').then(
            m => m.CustomerProfileComponent
          ),
        canDeactivate: [confirmDeactivateGuard],
        data: { pageHeader: true, noShadow: true },
      },
      {
        path: 'user-profile/:userId',
        loadComponent: () =>
          import('./pages/user-profile/user-profile.component').then(
            m => m.UserProfileComponent
          ),
        canDeactivate: [confirmDeactivateGuard],
        data: { pageHeader: true, noShadow: true },
      },
      {
        path: 'order-history',
        loadComponent: () =>
          import('./pages/order-history/order-history.component').then(
            m => m.OrderHistoryComponent
          ),
        data: { pageHeader: true, noShadow: true },
      },
      {
        path: 'downloads',
        loadComponent: () =>
          import('./pages/downloads/downloads.component').then(
            m => m.DownloadsComponent
          ),
        data: { pageHeader: true, noShadow: true },
      },
      {
        path: 'downloads/requirements-lists',
        loadComponent: () =>
          import(
            './pages/requirements-lists/requirements-lists.component'
          ).then(m => m.RequirementsListsComponent),
        data: { pageHeader: true, noShadow: true },
      },
      {
        path: 'downloads/communication',
        loadComponent: () =>
          import('./pages/communication/communication.component').then(
            m => m.CommunicationComponent
          ),
        data: { pageHeader: true, noShadow: true },
      },
    ],
  },
  {
    path: 'customer-select-transition',
    loadComponent: () =>
      import(
        './pages/customer-select-transition/customer-select-transition.component'
      ).then(m => m.CustomerSelectTransitionComponent),
    canActivate: [authGuard],
    data: { showHeader: false, showFooter: false },
  },
  {
    path: 'customer-select',
    loadComponent: () =>
      import('./pages/customer-select/customer-select.component').then(
        m => m.CustomerSelectComponent
      ),
    canActivate: [authGuard, customerSelectionRedirectGuard],
    data: { showHeader: false, showFooter: false },
  },
  {
    path: 'login/reset-password',
    loadComponent: () =>
      import('./pages/reset-password/reset-password.component').then(
        m => m.ResetPasswordComponent
      ),
    canActivate: [resetPasswordGuard, nonAuthGuard],
    data: { showHeader: false, showFooter: false },
  },
  {
    path: 'login/forgot-password',
    loadComponent: () =>
      import('./pages/forgot-password/forgot-password.component').then(
        m => m.ForgotPasswordComponent
      ),
    canActivate: [nonAuthGuard],
    data: { showHeader: false, showFooter: false },
  },
  {
    path: 'login',
    loadComponent: () =>
      import('./pages/login/login.page').then(m => m.LoginPage),
    canActivate: [nonAuthGuard],
    data: { showHeader: false, showFooter: false },
  },
  {
    path: '**',
    redirectTo: 'login',
  },
];
